import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Login.css';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000' });

function PasswordVisibilityToggle({ visible, onToggle, label }) {
  return (
    <button
      type="button"
      className="login-password-toggle"
      onClick={onToggle}
      aria-label={`${visible ? 'Hide' : 'Show'} ${label}`}
      aria-pressed={visible}
    >
      {visible ? (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8" />
          <path d="M9.9 5.2A10.7 10.7 0 0112 5c5.5 0 9 7 9 7a15.7 15.7 0 01-3.1 3.8M6.2 6.2C4.1 7.7 3 10 3 12c0 0 3.5 7 9 7 1.1 0 2.1-.3 3-.7" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 12s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );
}

export default function Login({ onLogin, resetToken: propResetToken }) {
  const [mode, setMode] = useState(propResetToken ? 'reset' : 'login'); // 'login' | 'register' | 'forgot' | 'reset'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetToken, setResetToken] = useState(propResetToken || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (propResetToken) {
      setResetToken(propResetToken);
      setMode('reset');
      setError('');
      setSuccess('');
    }
  }, [propResetToken]);

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
  };

  const handleAuthSubmit = async () => {
    if (!username.trim() || !password.trim()) return setError('Please fill in all fields');
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await API.post(`/api/auth/${mode}`, { username: username.trim(), password });
      localStorage.setItem('cms_token', res.data.token);
      localStorage.setItem('cms_user', JSON.stringify(res.data.user));
      onLogin(res.data.user);
    } catch(err) {
      setError(err.response?.data?.error || 'Something went wrong');
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!username.trim()) return setError('Please enter your username or email address');
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await API.post('/api/auth/forgot-password', { identifier: username.trim() });
      setSuccess(res.data.message || 'If an account exists, a reset link has been sent.');
    } catch(err) {
      setError(err.response?.data?.error || 'Failed to request password reset');
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!resetToken.trim()) return setError('Reset token is missing');
    if (!password.trim()) return setError('Please enter a new password');
    if (password.length < 8) return setError('Password must be at least 8 characters long');
    if (password !== confirmPassword) return setError('Passwords do not match');

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await API.post('/api/auth/reset-password', {
        token: resetToken.trim(),
        newPassword: password
      });
      setSuccess(res.data.message || 'Password reset successfully! You can now sign in.');
      setPassword('');
      setConfirmPassword('');
      setResetToken('');
      window.history.pushState({}, '', '/');
    } catch(err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
    setLoading(false);
  };

  const getTitle = () => {
    if (mode === 'login') return 'Sign in';
    if (mode === 'register') return 'Create account';
    if (mode === 'forgot') return 'Reset password';
    if (mode === 'reset') return 'Set new password';
  };

  const getSubtitle = () => {
    if (mode === 'login') return 'Welcome back! Enter your credentials.';
    if (mode === 'register') return 'First user becomes admin automatically.';
    if (mode === 'forgot') return 'Enter your username or email to receive a reset link.';
    if (mode === 'reset') return 'Enter and confirm your new password.';
  };

  return (
    <div className="login-bg">
      <div className="login-box">
        <div className="login-brand">
          <div className="login-brand-mark">SI3</div>
          <span className="login-brand-name">SI3 CMS</span>
        </div>
        <h2 className="login-title">{getTitle()}</h2>
        <p className="login-sub">{getSubtitle()}</p>

        {error && <div className="login-error">{error}</div>}
        {success && <div className="login-success">{success}</div>}

        {/* LOGIN & REGISTER FORMS */}
        {(mode === 'login' || mode === 'register') && (
          <>
            <label className="login-label">Username or Email</label>
            <input
              className="login-input"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAuthSubmit()}
              placeholder="Enter username or email"
              autoFocus
            />

            <div className="login-field-header">
              <label className="login-label">Password</label>
              {mode === 'login' && (
                <button
                  type="button"
                  className="login-forgot-link"
                  onClick={() => switchMode('forgot')}
                >
                  Forgot password?
                </button>
              )}
            </div>
            <div className="login-password-field">
              <input
                className="login-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAuthSubmit()}
                placeholder="Enter password"
              />
              <PasswordVisibilityToggle
                visible={showPassword}
                onToggle={() => setShowPassword(visible => !visible)}
                label="password"
              />
            </div>

            <button className="login-btn" onClick={handleAuthSubmit} disabled={loading}>
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>

            <div className="login-switch">
              {mode === 'login' ? (
                <>Don't have an account? <button type="button" onClick={() => switchMode('register')}>Register</button></>
              ) : (
                <>Already have an account? <button type="button" onClick={() => switchMode('login')}>Sign in</button></>
              )}
            </div>
          </>
        )}

        {/* FORGOT PASSWORD FORM */}
        {mode === 'forgot' && (
          <>
            <label className="login-label">Username or Email</label>
            <input
              className="login-input"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
              placeholder="Enter username or email"
              autoFocus
            />

            <button className="login-btn" onClick={handleForgotPassword} disabled={loading}>
              {loading ? 'Sending link...' : 'Send Reset Link'}
            </button>

            <div className="login-switch">
              Remembered your password? <button type="button" onClick={() => switchMode('login')}>Sign in</button>
            </div>
          </>
        )}

        {/* RESET PASSWORD FORM */}
        {mode === 'reset' && (
          <>
            {!propResetToken && (
              <>
                <label className="login-label">Reset Token</label>
                <input
                  className="login-input"
                  value={resetToken}
                  onChange={e => setResetToken(e.target.value)}
                  placeholder="Paste reset token from email"
                />
              </>
            )}

            <label className="login-label">New Password</label>
            <div className="login-password-field">
              <input
                className="login-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoFocus
              />
              <PasswordVisibilityToggle
                visible={showPassword}
                onToggle={() => setShowPassword(visible => !visible)}
                label="new password"
              />
            </div>

            <label className="login-label">Confirm New Password</label>
            <div className="login-password-field">
              <input
                className="login-input"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
                placeholder="Re-enter new password"
              />
              <PasswordVisibilityToggle
                visible={showConfirmPassword}
                onToggle={() => setShowConfirmPassword(visible => !visible)}
                label="confirm new password"
              />
            </div>

            <button className="login-btn" onClick={handleResetPassword} disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>

            <div className="login-switch">
              Back to <button type="button" onClick={() => { switchMode('login'); window.history.pushState({}, '', '/'); }}>Sign in</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
